import { ApiError } from "@/api";
import type { TraceEnvironment } from "@/components/conversations/conversationsSearch";
import type { TestCaseListItem } from "@/types";
import { DROPPED_REASON, droppedReasonText, isDropped, REACTIVATE_DROPPED_TEXT } from "./dropState";

/**
 * What SAMS may do to a TC, decided per operation.
 *
 * A bound, owner-scoped server says so itself in `management_capabilities` —
 * and then a prod TC can be editable, or batchable, or both, or neither, as
 * the server allows. Those flags are hints for the buttons; the API enforces
 * them, so a stale hint costs a refused request, not a wrong write.
 *
 * Without the field (older BE, unscoped modes) the legacy rule holds: a TC
 * read from the prod database (BE `9542b5c9`) is read-only — an edit is
 * refused with `PROD_TEST_CASE_READ_ONLY` and a Batch Test page holds dev TCs
 * only. `source_environment` is a free string, though, and a value that is
 * neither dev nor prod says nothing about which database answered, so it is
 * not treated as dev: neither operation is offered.
 */
export const PROD_READ_ONLY_REASON =
  "이 관리 서버에서는 prod TC를 조회만 할 수 있습니다";

export const UNKNOWN_ENV_READ_ONLY_REASON =
  "원본 환경이 dev · prod 중 무엇인지 알 수 없어 수정 · 실행을 막았습니다";

/** The server said no and did not say why, or said it in a code this build does not know. */
export const CAPABILITY_DENIED_REASON = "서버가 이 TC 에 대한 이 작업을 허용하지 않았습니다";

/**
 * BE's `read_only_reason` is a code — the edit's reason when edit is denied,
 * else the batch's. Words for the operator; the code stays out of the screen.
 */
const REASON_TEXT: Record<string, string> = {
  prod_read_only: PROD_READ_ONLY_REASON,
  source_environment_unknown: UNKNOWN_ENV_READ_ONLY_REASON,
  prod_batch_unverified: "prod TC 는 아직 Batch Test 에 넣을 수 없습니다 — prod 실행 경로가 검증되기 전입니다",
  retired: "은퇴한 TC 는 실행 대상이 아닙니다",
  no_replay_input: "재실행에 쓸 원본 입력이 없어 Batch Test 에 넣을 수 없습니다",
  dropped: DROPPED_REASON,
};

export function readOnlyReasonText(code: string | null | undefined): string {
  return (code && Object.prototype.hasOwnProperty.call(REASON_TEXT, code) && REASON_TEXT[code]) || CAPABILITY_DENIED_REASON;
}

/**
 * BE's refusal codes for a TC write (`detail.error.code`). Words for the
 * operator; any other failure keeps the server's message.
 */
const ERROR_CODE_TEXT: Record<string, string> = {
  TC_DROPPED: `${REACTIVATE_DROPPED_TEXT} — ${DROPPED_REASON}`,
};

export function testCaseErrorText(err: unknown): string {
  if (err instanceof ApiError && err.code && Object.prototype.hasOwnProperty.call(ERROR_CODE_TEXT, err.code)) {
    return ERROR_CODE_TEXT[err.code];
  }
  return err instanceof Error ? err.message : String(err);
}

export type TestCaseOperation = "edit" | "add_to_batch";

/** `reason` is set exactly when `allowed` is false. */
export type OperationGate = { allowed: true; reason: null } | { allowed: false; reason: string };

type Gated = Pick<TestCaseListItem, "source_environment" | "management_capabilities"> &
  Partial<Pick<TestCaseListItem, "is_dropped" | "drop_reason_code">>;

const ALLOWED: OperationGate = { allowed: true, reason: null };

export function testCaseGate(tc: Gated, operation: TestCaseOperation): OperationGate {
  // A dropped TC is never scheduled, whatever else is granted: offering it
  // would only repeat the same irrecoverable-input failure on every run.
  if (operation === "add_to_batch" && isDropped(tc)) {
    return { allowed: false, reason: droppedReasonText(tc.drop_reason_code) };
  }
  const capabilities = tc.management_capabilities;
  // Present means the server decided. Only a literal `true` opens the door —
  // a missing or malformed flag inside a present object stays shut.
  if (capabilities !== undefined && capabilities !== null && typeof capabilities === "object") {
    if (capabilities[operation] === true) return ALLOWED;
    return { allowed: false, reason: readOnlyReasonText(capabilities.read_only_reason) };
  }
  if (tc.source_environment === "dev") return ALLOWED;
  if (tc.source_environment === "prod") return { allowed: false, reason: PROD_READ_ONLY_REASON };
  return { allowed: false, reason: UNKNOWN_ENV_READ_ONLY_REASON };
}

/**
 * Which database Conversations should read the trace from — only a TC that
 * names one. Capabilities do not enter into it: being allowed to edit a TC
 * says nothing about where its source lives.
 */
export function traceEnvironment(tc: Pick<TestCaseListItem, "source_environment">): TraceEnvironment | null {
  if (tc.source_environment === "dev" || tc.source_environment === "prod") return tc.source_environment;
  return null;
}
